<template>
  <div
    class="nzsc-popup-overlay"
    @click="fnClose"
    @keydown.esc.stop.prevent="fnClose"
  >
    <div
      ref="dialog"
      class="nzsc-popup"
      :class="{ 'slide-up-anim': isAnimating }"
      role="dialog"
      tabindex="-1"
      aria-modal="true"
      aria-label="프로모션 안내"
      @click.stop
      @keydown.tab="fnTrapFocus"
    >
      <div
        ref="adTrigger"
        class="nzsc-popup__image-area"
        :class="{ 'nzsc-popup__image-area--fixed': isImageOnly }"
        role="button"
        tabindex="0"
        :aria-label="backgroundAlt || '광고 자세히 보기'"
        @click="fnClickAd"
        @keydown.enter.prevent="fnClickAd"
        @keydown.space.prevent="fnClickAd"
      >
        <img
          class="nzsc-popup__bg"
          :src="backgroundImg"
          :alt="backgroundAlt"
          :width="isImageOnly ? 375 : null"
          :height="isImageOnly ? 260 : null"
        />
        <div
          v-if="!isImageOnly"
          class="nzsc-popup__content"
          :style="{ color: textColor }"
        >
          <p
            class="nzsc-popup__main-text"
            v-html="mainText"
          ></p>
          <p class="nzsc-popup__sub-text">{{ subText }}</p>
          <div
            class="nzsc-popup__btn"
            :style="btnStyle"
          >
            자세히 보기
          </div>
        </div>
      </div>
      <div class="nzsc-popup__actions">
        <button
          type="button"
          class="nzsc-popup__action"
          @click="fnDismissToday"
        >
          오늘 하루 보지 않기
        </button>
        <button
          type="button"
          class="nzsc-popup__action"
          @click="fnClose"
        >
          닫기
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// 서체 선언은 공용 파일 한 곳에서만 한다. 여기서 @font-face 를 다시 쓰면(scoped 여도 전역이라)
// 같은 family 를 두고 서브셋 선언과 충돌해 통짜 폰트가 내려온다.
import "@/assets/css/firstam-font.css";
import { mapGetters, mapActions } from "vuex";
import { ViewRouter } from "@/utils/viewRoutes";
import { AD_TYPE } from "./adTypes";
import { clearFirstamReportContext } from "@/components/insurance/firstamerican/firstamEntryContext";
import {
  beginPopupNavigation,
  focusPopupEntry,
  restorePopupFocus,
  trapPopupFocus,
} from "./popupAdInteraction";

import rentCardBg from "@/assets/images/advertisement/popup/rent_card/background_img.svg";
import houseLoanBg from "@/assets/images/advertisement/popup/house_loan/background_img.svg";
import misoMovingBg from "@/assets/images/advertisement/popup/miso/moving/background_img.svg";
import misoInternetBg from "@/assets/images/advertisement/popup/miso/internet/background_img.svg";
import misoCleaningBg from "@/assets/images/advertisement/popup/miso/cleaning/background_img.svg";

const FIRSTAM_POPUP_CDN_URL =
  "https://image.kredatalab.com/firstamerican/img_popup_ad_fa.svg?v=20260724";

const AD_CONFIG = {
  [AD_TYPE.RENT_CARD]: {
    mainText: "이번 달 월세가<br/>부담된다면?",
    subText: "월세 카드 납부로 걱정 끝!",
    bg: rentCardBg,
    textColor: "#FFF",
    btnBackground: "linear-gradient(135deg, #24B6FF, #0076F4)",
    btnTextColor: "#FFF",
    viewEvent: "View AD Popup-Pay",
    clickEvent: "Click AD Popup-Pay",
  },
  [AD_TYPE.HOUSE_LOAN]: {
    mainText: "이번 달 최저 금리<br/>주담대 찾아드려요",
    subText: "조건 비교부터 상담까지 한 번에",
    bg: houseLoanBg,
    textColor: "#FFF",
    btnBackground: "#FFF",
    btnTextColor: "#004DA1",
    viewEvent: "View AD Popup-Loan",
    clickEvent: "Click AD Popup-Loan",
  },
  [AD_TYPE.MISO_MOVING]: {
    mainText: "이사 비용<br/>30만원 아껴요",
    subText: "다양한 업체에서 비교견적 받기",
    bg: misoMovingBg,
    textColor: "#FFF",
    btnBackground: "#FFF",
    btnTextColor: "#004DA1",
    viewEvent: "View AD Popup-Miso Move",
    clickEvent: "Click AD Popup-Miso Move",
  },
  [AD_TYPE.MISO_INTERNET]: {
    mainText: "인터넷 가입하면<br/>최대 48만원 현금 혜택",
    subText: "통신사별 현금 혜택을 비교해 보세요",
    bg: misoInternetBg,
    textColor: "#121212",
    btnBackground: "#C9E8FF",
    btnTextColor: "#0080FF",
    viewEvent: "View AD Popup-Miso Internet",
    clickEvent: "Click AD Popup-Miso Internet",
  },
  [AD_TYPE.MISO_CLEANING]: {
    mainText: "입주 청소<br/>평균 3만원 절약해요",
    subText: "견적 비교로 합리적인 선택이 가능해요",
    bg: misoCleaningBg,
    textColor: "#121212",
    btnBackground: "#C9E8FF",
    btnTextColor: "#0080FF",
    viewEvent: "View AD Popup-Miso Clean",
    clickEvent: "Click AD Popup-Miso Clean",
  },
  [AD_TYPE.FIRSTAM]: {
    bg: FIRSTAM_POPUP_CDN_URL,
    backgroundAlt: "매매사기, 권원보험으로 예방하세요",
    imageOnly: true,
    viewEvent: "View Sale Firstam-Popup",
    clickEvent: "Click Sale Firstam-Popup",
  },
};

const SUSPEND_KEY = "ad_popup_suspend_until";

export default {
  name: "PopupAdComponent",
  data() {
    return {
      isAnimating: true,
      isNavigating: false,
      previouslyFocusedElement: null,
    };
  },
  computed: {
    ...mapGetters("advertisementStore", ["resolvedAdType", "encryptedCartSeq"]),
    adConfig() {
      return AD_CONFIG[this.resolvedAdType] || null;
    },
    backgroundImg() {
      return this.adConfig?.bg || "";
    },
    backgroundAlt() {
      return this.adConfig?.backgroundAlt || "";
    },
    isImageOnly() {
      return this.adConfig?.imageOnly === true;
    },
    mainText() {
      return this.adConfig?.mainText || "";
    },
    subText() {
      return this.adConfig?.subText || "";
    },
    textColor() {
      return this.adConfig?.textColor || "#FFF";
    },
    btnStyle() {
      const config = this.adConfig;
      if (!config) return {};
      return {
        background: config.btnBackground,
        color: config.btnTextColor,
      };
    },
  },
  mounted() {
    this.previouslyFocusedElement = document.activeElement;
    // 다이얼로그 자체로 초점을 이동해 이미지 클릭 영역의 브라우저 기본 outline을 노출하지 않는다.
    this.$nextTick(() => focusPopupEntry(this.$refs.dialog));
    if (this.adConfig?.viewEvent) {
      this.$mixpanel_track(this.adConfig.viewEvent);
    }
  },
  beforeDestroy() {
    restorePopupFocus(this.previouslyFocusedElement);
  },
  methods: {
    ...mapActions("reportStore", ["acReportPre"]),
    fnSetSuspend(minutes) {
      const until = new Date(Date.now() + minutes * 60 * 1000).toISOString();
      localStorage.setItem(SUSPEND_KEY, until);
    },
    fnDismissToday() {
      this.fnSetSuspend(60 * 24); // 24시간
      this.$emit("close");
    },
    fnClose() {
      this.fnSetSuspend(10); // 10분
      this.$emit("close");
    },
    fnTrapFocus(event) {
      trapPopupFocus(event, this.$refs.dialog, document.activeElement);
    },
    async fnClickAd() {
      if (!this.adConfig || !beginPopupNavigation(this)) return;
      this.$mixpanel_track(this.adConfig.clickEvent);

      const adType = this.resolvedAdType;

      if (adType === AD_TYPE.RENT_CARD) {
        this.$emit("close");
        this.$router.push(ViewRouter.getDobyLandingRoute());
      } else if (adType === AD_TYPE.HOUSE_LOAN) {
        if (this.encryptedCartSeq) {
          try {
            const report = await this.acReportPre(encodeURIComponent(this.encryptedCartSeq));
            this.$router.push(ViewRouter.getHouseLoanConsultationRoute(report?.reportSeq));
          } catch (error) {
            window.console.error(error);
          }
        }
        this.$emit("close");
      } else if (adType === AD_TYPE.MISO_MOVING) {
        this.$emit("close");
        window.open(
          ViewRouter.getMisoRoute({ miso_type: "moving" }),
          "_blank",
          "noopener,noreferrer",
        );
      } else if (adType === AD_TYPE.MISO_INTERNET) {
        this.$emit("close");
        window.open(
          ViewRouter.getMisoRoute({ miso_type: "internet" }),
          "_blank",
          "noopener,noreferrer",
        );
      } else if (adType === AD_TYPE.MISO_CLEANING) {
        this.$emit("close");
        window.open(
          ViewRouter.getMisoRoute({ miso_type: "cleaning" }),
          "_blank",
          "noopener,noreferrer",
        );
      } else if (adType === AD_TYPE.FIRSTAM) {
        clearFirstamReportContext(sessionStorage);
        this.$emit("close");
        this.$router.push("/insurance/firstamerican/title-001/landing");
      }
    },
  },
};
</script>

<style scoped>
/* @font-face 는 src/assets/css/firstam-font.css 한 곳에서만 선언한다(<script> 에서 import).
   여기서 다시 선언하면 scoped 여도 전역이라 서브셋 선언과 같은 family 를 두고 충돌한다. */

.nzsc-popup-overlay {
  position: fixed;
  top: 0;
  right: max(calc(25vw - 340px), 0px);
  width: 40%;
  max-width: 450px;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 30000000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.nzsc-popup {
  width: 100%;
  max-width: 450px;
  outline: 0;
  border-radius: 20px 20px 0 0;
  overflow: hidden;
  background: #fff;
  transform: translateY(100%);
  font-family: "Firstam Noto Sans KR", "Noto Sans KR", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
}

.nzsc-popup.slide-up-anim {
  animation: popupSlideUp 0.3s ease-out forwards;
}

@keyframes popupSlideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.nzsc-popup__image-area {
  position: relative;
  width: 100%;
  cursor: pointer;
  overflow: hidden;
}
.nzsc-popup__image-area--fixed {
  aspect-ratio: 375 / 260;
}

.nzsc-popup__bg {
  width: 100%;
  height: auto;
  display: block;
}

.nzsc-popup__content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 30px 24px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  box-sizing: border-box;
}

.nzsc-popup__main-text {
  font-size: 25px;
  font-weight: 700;
  line-height: 34px;
  margin-bottom: 8px;
}

.nzsc-popup__sub-text {
  font-size: 15px;
  font-weight: 500;
  line-height: normal;
  margin-bottom: 16px;
}

.nzsc-popup__btn {
  display: inline-flex;
  align-self: flex-start;
  padding: 10px 14px;
  border-radius: 51px;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;
}

.nzsc-popup__actions {
  display: flex;
  justify-content: space-between;
  padding: 18px 24px;
  background: #fff;
  height: 54px;
  box-sizing: border-box;
}

.nzsc-popup__action {
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: #979797;
  cursor: pointer;
}

@media screen and (max-width: 1080px) {
  .nzsc-popup-overlay {
    left: 0;
    right: auto;
    width: 100%;
    max-width: 100%;
    justify-content: center;
  }
  .nzsc-popup {
    max-width: 100%;
  }
}
</style>
